import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./RozetkaPage.css";
import RozetkaTopBar from "../components/marketplace/RozetkaTopBar";
import SearchableEntityDropdown from "../components/marketplace/SearchableEntityDropdown";
import MappingStatusBadge from "../components/marketplace/MappingStatusBadge";
import RozetkaAttributeMappingPanel from "../components/RozetkaAttributeMappingPanel";
import {
    getRozetkaAccounts,
    getRozetkaCategoryMappings,
    getRozetkaInternalBrands,
    getRozetkaInternalCategories,
    getRozetkaProducerMappings,
    getRozetkaReferenceCategories,
    getRozetkaReferenceProducers,
    syncRozetkaCategories,
    upsertRozetkaCategoryMapping,
    upsertRozetkaProducerMapping,
} from "../api/rozetkaApi";

// Категория считается смапленной, если у неё есть rz_category_id; бренд -- по
// булеву флагу mapped, который уже считает бэкенд (см. producer_service
// .list_internal_brands_for_mapping).
const isCategoryMapped = (row) => Boolean(row.rz_category_id);

const initialTabFor = (location) => {
    if (location.pathname.endsWith("/attributes")) return "attributes";
    return new URLSearchParams(location.search).get("tab") === "brands" ? "brands" : "categories";
};

// Аккаунт приходит по ссылке с главной страницы Rozetka (кнопки Категории/
// Бренды/Характеристики уже несут выбранный там аккаунт) -- здесь его больше
// не выбирают повторно, чтобы не дублировать выбор и не рассинхронизироваться
// с тем, что было выбрано на /integrations/rozetka.
const initialAccountFor = (location) => new URLSearchParams(location.search).get("account") || "";

const RozetkaMappingPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [tab, setTab] = useState(() => initialTabFor(location));

    const [accounts, setAccounts] = useState([]);
    const [accountId, setAccountId] = useState(() => initialAccountFor(location));
    const [message, setMessage] = useState("");

    const [categoryMappings, setCategoryMappings] = useState([]);
    const [unmappedCategories, setUnmappedCategories] = useState([]);
    const [categoryDraft, setCategoryDraft] = useState({ internal_category_code: "", rz_category_id: "" });
    const [internalCategoryQuery, setInternalCategoryQuery] = useState("");

    const [producerMappings, setProducerMappings] = useState([]);
    const [unmappedBrands, setUnmappedBrands] = useState([]);
    const [brandDraft, setBrandDraft] = useState({ internal_brand_code: "", rz_producer_id: "", title: "" });
    const [internalBrandQuery, setInternalBrandQuery] = useState("");

    const [attrCategoryId, setAttrCategoryId] = useState("");
    const [attrCategoryName, setAttrCategoryName] = useState("Категория Rozetka");

    const selectedAccount = accounts.find((row) => String(row.id) === String(accountId));
    // Уникальные уже смапленные категории Rozetka (для быстрого выбора на
    // вкладке "Характеристики" -- без поиска по всему справочнику Rozetka).
    const mappedRozetkaCategories = Array.from(
        new Map(categoryMappings.filter((row) => row.platform_category_id).map((row) => [row.platform_category_id, row])).values(),
    );

    const selectTab = (key) => {
        setTab(key);
        // Держим адресную строку в синхронизации с локальным табом -- иначе
        // обновление страницы вернёт на другую вкладку (categories/attributes
        // живут на разных pathname, см. IntegrationsPage.jsx).
        const params = new URLSearchParams();
        if (accountId) params.set("account", accountId);
        if (key !== "attributes") params.set("tab", key);
        const path = key === "attributes" ? "/integrations/rozetka/attributes" : "/integrations/rozetka/mappings";
        navigate(`${path}${params.toString() ? `?${params.toString()}` : ""}`, { replace: true });
    };

    const loadCategoryMappings = useCallback(async (id) => {
        if (!id) return;
        setCategoryMappings((await getRozetkaCategoryMappings(id)) || []);
    }, []);

    const loadUnmappedCategories = useCallback(async (id) => {
        if (!id) return;
        const rows = (await getRozetkaInternalCategories(Number(id), { limit: 1000 })) || [];
        setUnmappedCategories(rows.filter((row) => !isCategoryMapped(row)));
    }, []);

    const loadProducerMappings = useCallback(async (id) => {
        if (!id) return;
        setProducerMappings((await getRozetkaProducerMappings(id)) || []);
    }, []);

    const loadUnmappedBrands = useCallback(async (id) => {
        if (!id) return;
        const rows = (await getRozetkaInternalBrands(Number(id), { limit: 1000 })) || [];
        setUnmappedBrands(rows.filter((row) => !row.mapped));
    }, []);

    useEffect(() => {
        getRozetkaAccounts()
            .then((rows) => {
                setAccounts(rows || []);
                setAccountId((current) => current || String(rows?.[0]?.id || ""));
            })
            .catch(() => setMessage("Не удалось загрузить аккаунты Rozetka."));
    }, []);

    useEffect(() => {
        loadCategoryMappings(Number(accountId)).catch((error) => setMessage(error?.response?.data?.detail || "Не удалось загрузить mapping категорий."));
        loadUnmappedCategories(Number(accountId)).catch((error) => setMessage(error?.response?.data?.detail || "Не удалось загрузить категории без маппинга."));
        loadProducerMappings(Number(accountId)).catch((error) => setMessage(error?.response?.data?.detail || "Не удалось загрузить маппинг брендов."));
        loadUnmappedBrands(Number(accountId)).catch((error) => setMessage(error?.response?.data?.detail || "Не удалось загрузить бренды без маппинга."));
    }, [accountId, loadCategoryMappings, loadUnmappedCategories, loadProducerMappings, loadUnmappedBrands]);

    const fetchInternalCategories = useCallback(
        (search) => getRozetkaInternalCategories(Number(accountId), { search: search || undefined, level: 2, limit: 100 }),
        [accountId],
    );
    const fetchPlatformCategories = useCallback(
        (search) => getRozetkaReferenceCategories(Number(accountId), { search: search || undefined, limit: 100 }),
        [accountId],
    );
    const fetchInternalBrands = useCallback(
        (search) => getRozetkaInternalBrands(Number(accountId), { search: search || undefined, limit: 100 }),
        [accountId],
    );
    const fetchProducers = useCallback(
        (search) => getRozetkaReferenceProducers(Number(accountId), { search: search || undefined, limit: 100 }),
        [accountId],
    );

    const saveCategoryMapping = async () => {
        await upsertRozetkaCategoryMapping(Number(accountId), {
            internal_category_code: categoryDraft.internal_category_code,
            platform_category_id: categoryDraft.rz_category_id,
            is_active: true,
        });
        setMessage("Связь категорий сохранена.");
        await Promise.all([loadCategoryMappings(Number(accountId)), loadUnmappedCategories(Number(accountId))]);
    };

    const pickUnmappedCategory = (row) => {
        setCategoryDraft((current) => ({ ...current, internal_category_code: row.internal_category_code }));
        setInternalCategoryQuery(row.internal_category_name || row.internal_category_code);
        setMessage(`Выбрана категория «${row.internal_category_name || row.internal_category_code}» — укажите категорию Rozetka справа и нажмите «Связать».`);
    };

    const runSyncCategories = async () => {
        setMessage("Синхронизация категорий…");
        try {
            const result = await syncRozetkaCategories(Number(accountId));
            setMessage(`Категорий синхронизировано: ${result.items_count}.`);
        } catch (error) {
            setMessage(error?.response?.data?.detail || "Не удалось синхронизировать категории.");
        }
    };

    const saveProducerMapping = async () => {
        await upsertRozetkaProducerMapping(Number(accountId), {
            internal_brand_code: brandDraft.internal_brand_code,
            rz_producer_id: Number(brandDraft.rz_producer_id),
            status: "approved",
        });
        setMessage("Связь бренда сохранена.");
        setBrandDraft({ internal_brand_code: "", rz_producer_id: "", title: "" });
        setInternalBrandQuery("");
        await Promise.all([loadProducerMappings(Number(accountId)), loadUnmappedBrands(Number(accountId))]);
    };

    const pickUnmappedBrand = (row) => {
        setBrandDraft((current) => ({ ...current, internal_brand_code: row.internal_brand_code }));
        setInternalBrandQuery(row.internal_brand_code);
        setMessage(`Выбран бренд «${row.internal_brand_code}» — укажите производителя Rozetka справа и нажмите «Связать».`);
    };

    return (
        <div className="rozetka-page" style={{ display: "grid", gap: "16px" }}>
            <RozetkaTopBar
                activeKey={tab}
                accountId={accountId}
                localKeys={["categories", "brands", "attributes"]}
                onLocalSelect={selectTab}
            />
            {message && <div className="rzm-status">{message}</div>}

                {tab === "categories" && (
                    <>
                        <section className="rzm-card">
                            <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
                                <h2>Категории</h2>
                                <button type="button" className="rzm-btn" onClick={() => runSyncCategories().catch(() => {})}>
                                    Синхронизировать категории Rozetka
                                </button>
                            </div>
                            <p className="rzm-hint">
                                «Наша категория» показывает только категории, у которых уже есть товары-кандидаты для
                                Rozetka (нужны активные отправители на вкладке «Отправители/цены» + хотя бы одна генерация
                                фида на вкладке «Выгрузка» — она же пересобирает список кандидатов). Пустой список слева —
                                обычно значит, что фид ещё ни разу не генерировался.
                            </p>
                            <div className="rzm-grid-3">
                                <SearchableEntityDropdown
                                    label="Наша категория"
                                    placeholder="Например: вітамін, 2644"
                                    fetchFn={fetchInternalCategories}
                                    getKey={(row) => row.internal_category_code}
                                    renderLabel={(row) => `${row.internal_category_code} · ${row.internal_category_name} · ${row.products_count} товаров`}
                                    selectedKey={categoryDraft.internal_category_code}
                                    onPick={(row) => setCategoryDraft({ ...categoryDraft, internal_category_code: row.internal_category_code })}
                                    initialQuery={internalCategoryQuery}
                                />
                                <SearchableEntityDropdown
                                    label="Категория Rozetka"
                                    placeholder="Например: біодобав, 23005"
                                    fetchFn={fetchPlatformCategories}
                                    getKey={(row) => row.rz_category_id}
                                    renderLabel={(row) => `${row.rz_category_id} · ${row.title_ua}`}
                                    selectedKey={categoryDraft.rz_category_id}
                                    onPick={(row) => setCategoryDraft({ ...categoryDraft, rz_category_id: row.rz_category_id })}
                                />
                                <div style={{ display: "grid", gap: "6px" }}>
                                    <span style={{ visibility: "hidden", fontWeight: 700 }}>_</span>
                                    <button
                                        type="button"
                                        className="rzm-btn rzm-btn-primary"
                                        disabled={!categoryDraft.internal_category_code || !categoryDraft.rz_category_id}
                                        onClick={() => saveCategoryMapping().catch((error) => setMessage(error?.response?.data?.detail || "Не удалось сохранить mapping."))}
                                    >
                                        Связать
                                    </button>
                                </div>
                            </div>
                        </section>

                        <section className="rzm-card">
                            <h2>Категории без маппинга ({unmappedCategories.length})</h2>
                            <p className="rzm-hint">
                                Категории с товарами в наличии, у которых нет связи с Rozetka. Их товары не попадают в фид. Отсортированы по числу товаров.
                            </p>
                            {unmappedCategories.length === 0 ? (
                                <div className="rzm-status">Все категории с товарами смаплены ✓</div>
                            ) : (
                                <div className="rzm-list" style={{ maxHeight: "320px" }}>
                                    {unmappedCategories.map((row) => (
                                        <button key={row.internal_category_code} type="button" className="rzm-list-item" onClick={() => pickUnmappedCategory(row)}>
                                            <span>
                                                {row.internal_category_code} · {row.internal_category_name || ""}
                                            </span>
                                            <span className="rzm-pill">{row.products_count} тов. · Смапити →</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </section>

                        <section className="rzm-card">
                            <h2>Текущие связи категорий ({categoryMappings.length})</h2>
                            <table className="rzm-table">
                                <thead>
                                    <tr>
                                        <th>Наша категория</th>
                                        <th>Категория Rozetka</th>
                                        <th>Статус</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {categoryMappings.map((row) => (
                                        <tr key={row.id}>
                                            <td>
                                                {row.internal_category_code} · {row.internal_category_name || ""}
                                            </td>
                                            <td>
                                                {row.platform_category_id} · {row.platform_category_title || ""}
                                            </td>
                                            <td>
                                                <MappingStatusBadge status={row.is_active ? "mapped" : "ignored"} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </section>
                    </>
                )}

                {tab === "brands" && (
                    <>
                        <section className="rzm-card">
                            <h2>Бренды / производители</h2>
                            <p className="rzm-hint">
                                Справочник производителей у Rozetka общий на весь маркетплейс (сотни тысяч записей) —
                                предварительной синхронизации нет. Поле «Производитель Rozetka» ищет напрямую в Rozetka.
                                «Наш бренд» — это значение поля «Виробник» из нашего каталога, у которого уже есть
                                товары-кандидаты для Rozetka.
                            </p>
                            <div className="rzm-grid-3">
                                <SearchableEntityDropdown
                                    label="Наш бренд/ТМ"
                                    placeholder="Например: Now Foods"
                                    fetchFn={fetchInternalBrands}
                                    getKey={(row) => row.internal_brand_code}
                                    renderLabel={(row) => `${row.internal_brand_code} · ${row.products_count} товаров`}
                                    selectedKey={brandDraft.internal_brand_code}
                                    onPick={(row) => setBrandDraft({ ...brandDraft, internal_brand_code: row.internal_brand_code })}
                                    initialQuery={internalBrandQuery}
                                />
                                <SearchableEntityDropdown
                                    label="Производитель Rozetka"
                                    placeholder="Например: Now"
                                    fetchFn={fetchProducers}
                                    getKey={(row) => row.id}
                                    renderLabel={(row) => `${row.rz_producer_id} · ${row.title}`}
                                    selectedKey={brandDraft.rz_producer_id}
                                    onPick={(row) => setBrandDraft({ ...brandDraft, rz_producer_id: row.id, title: row.title })}
                                />
                                <div style={{ display: "grid", gap: "6px" }}>
                                    <span style={{ visibility: "hidden", fontWeight: 700 }}>_</span>
                                    <button
                                        type="button"
                                        className="rzm-btn rzm-btn-primary"
                                        disabled={!brandDraft.internal_brand_code.trim() || !brandDraft.rz_producer_id}
                                        onClick={() => saveProducerMapping().catch((error) => setMessage(error?.response?.data?.detail || "Не удалось сохранить mapping бренда."))}
                                    >
                                        Связать
                                    </button>
                                </div>
                            </div>
                        </section>

                        <section className="rzm-card">
                            <h2>Бренды без маппинга ({unmappedBrands.length})</h2>
                            <p className="rzm-hint">
                                Бренды с товарами-кандидатами для Rozetka, у которых ещё нет связи с производителем Rozetka.
                            </p>
                            {unmappedBrands.length === 0 ? (
                                <div className="rzm-status">Все бренды с товарами смаплены ✓</div>
                            ) : (
                                <div className="rzm-list" style={{ maxHeight: "320px" }}>
                                    {unmappedBrands.map((row) => (
                                        <button key={row.internal_brand_code} type="button" className="rzm-list-item" onClick={() => pickUnmappedBrand(row)}>
                                            <span>{row.internal_brand_code}</span>
                                            <span className="rzm-pill">{row.products_count} тов. · Смапити →</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </section>

                        <section className="rzm-card">
                            <h2>Текущие связи брендов ({producerMappings.length})</h2>
                            <table className="rzm-table">
                                <thead>
                                    <tr>
                                        <th>Наш бренд</th>
                                        <th>Производитель Rozetka</th>
                                        <th>Статус</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {producerMappings.map((row) => (
                                        <tr key={row.id}>
                                            <td>{row.internal_brand_code}</td>
                                            <td>
                                                {row.rz_producer_id} · {row.producer_title || ""}
                                            </td>
                                            <td>
                                                <MappingStatusBadge status={row.status} />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </section>
                    </>
                )}

                {tab === "attributes" && (
                    <>
                        <section className="rzm-card">
                            <h2>Характеристики</h2>
                            <p className="rzm-hint">Выберите категорию Rozetka — ниже появится её схема характеристик и mapping с master catalog.</p>
                            <div className="rzm-grid-2">
                                <div className="rzm-field">
                                    Уже смапленные категории ({mappedRozetkaCategories.length})
                                    <div className="rzm-list" style={{ maxHeight: "220px" }}>
                                        {mappedRozetkaCategories.length === 0 ? (
                                            <span className="rzm-hint">Пока нет смапленных категорий — смапьте хотя бы одну на вкладке «Категории».</span>
                                        ) : (
                                            mappedRozetkaCategories.map((row) => (
                                                <button
                                                    key={row.platform_category_id}
                                                    type="button"
                                                    className={`rzm-list-item${String(row.platform_category_id) === String(attrCategoryId) ? " sel" : ""}`}
                                                    onClick={() => {
                                                        setAttrCategoryId(row.platform_category_id);
                                                        setAttrCategoryName(row.platform_category_title || row.platform_category_id);
                                                    }}
                                                >
                                                    <span>
                                                        {row.platform_category_id} · {row.platform_category_title || "без названия"}
                                                    </span>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                </div>
                                <SearchableEntityDropdown
                                    label="Или найти любую категорию Rozetka"
                                    placeholder="Например: вітамін, 274789"
                                    fetchFn={fetchPlatformCategories}
                                    getKey={(row) => row.rz_category_id}
                                    renderLabel={(row) => `${row.rz_category_id} · ${row.title_ua}`}
                                    selectedKey={attrCategoryId}
                                    onPick={(row) => {
                                        setAttrCategoryId(row.rz_category_id);
                                        setAttrCategoryName(row.title_ua);
                                    }}
                                />
                            </div>
                        </section>
                        {attrCategoryId && (
                            <RozetkaAttributeMappingPanel
                                accountId={Number(accountId)}
                                categoryId={attrCategoryId}
                                categoryName={attrCategoryName}
                                onStatus={setMessage}
                            />
                        )}
                    </>
                )}
        </div>
    );
};

export default RozetkaMappingPage;
